import { getMinWidthMedia, getSafeURL } from '@utils/utils';

export type WebImageSizes = keyof Global.WebImageSizes;
export type MobileImageSizes = keyof Global.MobileImageSizes;
export type ImageRatios = 'x1' | 'x2';
export type Image = {
    urlAndFormat: Global.ImageSizeUrl;
    width: number;
    height: number;
};

export type PreloadCandidate = {
    href: string;
    imageSrcSet: string;
    media: string;
    type: string;
};

export const imageFormatsInOrder: Global.ImageFormat[] = ['svg', 'avif', 'webp', 'png', 'jpeg'];
export const imageRatioMap: Record<ImageRatios, number> = { x1: 0, x2: 1 };

/**
 * Собирает один ImageSet: large и medium берутся из десктопного набора,
 * small — из мобильного, blur — из десктопного. Нужно, чтобы hero рендерился
 * одним <picture>: браузер по media у <source> скачивает ровно один вариант,
 * а не десктопный и мобильный сразу. Вариант с двумя <picture> и display: none
 * не годится — скрытые <img> всё равно загружаются.
 */
export const mergeResponsiveImageSets = (
    desktop: Global.ImageSet | null | undefined,
    mobile: Global.ImageSet | null | undefined,
): Global.ImageSet | null => {
    const desktopWeb = desktop?.web;
    const mobileWeb = mobile?.web;

    if (desktopWeb === undefined) return mobile ?? null;
    if (mobileWeb === undefined) return desktop ?? null;

    return {
        web: {
            blur: desktopWeb.blur,
            small: mobileWeb.small,
            medium: desktopWeb.medium,
            large: desktopWeb.large,
        },
    };
};

export const extractImagesWithRatio = (images: Global.Images, ratio: ImageRatios): Image[] => {
    const image = images[imageRatioMap[ratio]];

    return image.urls.map((url) => ({
        urlAndFormat: url,
        height: image.height,
        width: image.width,
    }));
};

export const sortImageFormats = (formatsInOrder: Global.ImageFormat[]) => (a: Image, b: Image) =>
    formatsInOrder.indexOf(a.urlAndFormat.format) - formatsInOrder.indexOf(b.urlAndFormat.format);

export const extractWebImages = (
    images: Global.WebImageSizes,
    size: WebImageSizes,
    ratio: ImageRatios,
): Image[] =>
    extractImagesWithRatio(
        images[size].sort((a, b) => a.width - b.width),
        ratio,
    );

export const extractMobileImages = (images: Global.MobileImageSizes, ratio: ImageRatios): Image[] =>
    extractImagesWithRatio(
        images.single.sort((a, b) => a.width - b.width),
        ratio,
    );

export const getFirstURL = (images: Image[]) => {
    const [first] = images;
    if (first === undefined) {
        console.error('Image not found');
        return '';
    }
    return first.urlAndFormat.url;
};

export const getSRC = (url: Global.ImageSizeUrl['url']) =>
    typeof url === 'string' ? url : url.src;

export const getPreviewSRC = (sources: Global.WebImageSizes): string | null => {
    const extract = (size: WebImageSizes) =>
        getFirstURL(
            extractWebImages(sources, size, 'x1').sort(sortImageFormats(imageFormatsInOrder)),
        );
    const { blur } = sources;
    const blurURL = blur ? extract('blur') : undefined;
    if (blurURL && typeof blurURL === 'string') return blurURL;
    const url = extract('small');
    if (typeof url === 'string') return null;
    const { blurDataURL } = url;
    if (blurDataURL === undefined) return null;
    return blurDataURL;
};

export const defaultMediaQueries: Record<WebImageSizes, number> = {
    blur: 0,
    small: 0,
    medium: 640,
    large: 900,
};

type WebPreloadSize = Exclude<WebImageSizes, 'blur'>;

const webPreloadSizes: WebPreloadSize[] = ['large', 'medium', 'small'];

const getWebPreloadMedia = (
    size: WebPreloadSize,
    mediaQueries: Record<WebImageSizes, number>,
): string => {
    const minWidthMedia = getMinWidthMedia(mediaQueries[size]);
    const sizeIndex = webPreloadSizes.indexOf(size);
    const nextSize: WebPreloadSize | undefined = webPreloadSizes[sizeIndex - 1];

    if (nextSize === undefined) return minWidthMedia;

    const maxWidthMedia = `(max-width: ${mediaQueries[nextSize] - 1}px)`;

    return mediaQueries[size] === 0 ? maxWidthMedia : `${minWidthMedia} and ${maxWidthMedia}`;
};

const getRangeMedia = (
    sizes: WebPreloadSize[],
    mediaQueries: Record<WebImageSizes, number>,
): string => {
    const requested = webPreloadSizes.filter((size) => sizes.includes(size));
    const largest = requested[0];
    const smallest = requested[requested.length - 1];

    if (largest === undefined || smallest === undefined) return '';

    const largestIndex = webPreloadSizes.indexOf(largest);
    const above: WebPreloadSize | undefined = webPreloadSizes[largestIndex - 1];
    const minWidthMedia = getMinWidthMedia(mediaQueries[smallest]);
    const maxWidthMedia =
        above === undefined ? undefined : `(max-width: ${mediaQueries[above] - 1}px)`;

    if (maxWidthMedia === undefined) return minWidthMedia;

    return mediaQueries[smallest] === 0 ? maxWidthMedia : `${minWidthMedia} and ${maxWidthMedia}`;
};

const createPreloadCandidate = (
    image1x: Image | undefined,
    image2x: Image | undefined,
    media: string,
    shouldInclude1xInSrcSet: boolean,
): PreloadCandidate | undefined => {
    if (image1x === undefined) return undefined;

    const href = getSafeURL(getSRC(image1x.urlAndFormat.url));
    const image2xURL = image2x ? getSafeURL(getSRC(image2x.urlAndFormat.url)) : href;

    return {
        href,
        imageSrcSet: shouldInclude1xInSrcSet ? `${image2xURL} 2x, ${href}` : `${image2xURL} 2x`,
        media,
        type: `image/${image1x.urlAndFormat.format}`,
    };
};

const createSourcePreloadCandidate = (
    images1x: Image[],
    images2x: Image[],
    media: string,
): PreloadCandidate | undefined => {
    const [image1x] = images1x.sort(sortImageFormats(imageFormatsInOrder));
    const image2x = images2x.find(
        ({ urlAndFormat }) => urlAndFormat.format === image1x?.urlAndFormat.format,
    );

    return createPreloadCandidate(image1x, image2x, media, true);
};

// Мобильные <source> в Picture.tsx отдают один 1x URL без дескриптора плотности.
// Preload обязан совпадать с ними, иначе на DPR >= 2 картинка скачается дважды.
const createMobilePreloadCandidate = (
    images1x: Image[],
    media: string,
): PreloadCandidate | undefined => {
    const [image1x] = images1x;

    if (image1x === undefined) return undefined;

    const href = getSafeURL(getSRC(image1x.urlAndFormat.url));

    return {
        href,
        imageSrcSet: href,
        media,
        type: `image/${image1x.urlAndFormat.format}`,
    };
};

export const pickPreloadCandidates = (
    sources: Global.ImageSet,
    mediaQueries: Record<WebImageSizes, number> = defaultMediaQueries,
    sizes: WebImageSizes[] = webPreloadSizes,
): PreloadCandidate[] => {
    if (sources.web !== undefined) {
        const webSources = sources.web;

        return webPreloadSizes
            .filter((size) => sizes.includes(size))
            .flatMap((size) => {
                const images1x = extractWebImages(webSources, size, 'x1');
                const images2x = extractWebImages(webSources, size, 'x2');
                const media = getWebPreloadMedia(size, mediaQueries);
                const candidate = createSourcePreloadCandidate(images1x, images2x, media);

                return candidate === undefined ? [] : [candidate];
            });
    }

    if (sources.mobile !== undefined) {
        const requestedSizes = webPreloadSizes.filter((size) => sizes.includes(size));

        if (requestedSizes.length === 0) return [];

        const candidate = createMobilePreloadCandidate(
            extractMobileImages(sources.mobile, 'x1'),
            getRangeMedia(requestedSizes, mediaQueries),
        );

        return candidate === undefined ? [] : [candidate];
    }

    return [];
};
